import TRTCJsSdk, { Client, LocalStream, Stream, RemoteStream, TurnServerConfig, RtcError } from 'trtc-js-sdk';
// @ts-ignore
import RTCAIDenoiser from 'rtc-ai-denoiser';
import pick from 'lodash/pick';
import { Span, context, trace } from '@opentelemetry/api';
import Recorder from './recorder';
import { createLocalStream } from '../tccc/createLocalStream';
import { Direction } from '../constants/sessions';
import { workerInterval, clearWorkerInterval } from 'tccc-utils';
import { TcccSdk } from '../tccc';
import { Asr } from './asr/Asr';
import { ConnectionState } from '../constants/trtc';
import { contextWithSpan, tracer } from '../http/tracer';
import { SessionType } from '../store/sessions';
import forEach from 'lodash/forEach';
import { aegis } from '../http/aegis';
import { getLogger } from '../common/Logger';
import { UserInfo } from '../tccc/Agent';
import i18next from '../i18n/i18next.config';

const IVR_USER = 'IVR_USER';
const rtcAIDenoiser = new RTCAIDenoiser({
  assetsPath: 'https://tccc.qcloud.com/assets/RTCAIDenoiser/1.1.6',
});

TRTCJsSdk.Logger.setLogLevel(TRTCJsSdk.Logger.LogLevel.ERROR);

function addSpanLog(name: string, attributes?: Record<string, string>) {
  return (
    target: any,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(parentSpan: Span, ...params: any[]) => Promise<any>>,
  ) => {
    const func = descriptor.value;
    descriptor.value = async function (parentSpan: Span, ...args: any[]) {
      const userInfo = (this as WebRtc)?.emitter?.Agent?.userInfo || null;
      const span = tracer.startSpan(
        name,
        {
          attributes: {
            ...pick(userInfo, ['sdkAppId', 'userId']),
            ...attributes,
          },
        },
        trace.setSpan(context.active(), parentSpan),
      );
      try {
        // @ts-ignore
        const result = await func.apply(this, [parentSpan, ...args]);
        span.end();
        return result;
      } catch (e) {
        const error = e as RtcError;
        const errorCode = error?.getCode?.() || 0;
        span.setAttributes({
          errorName: error.name,
          errorMessage: error.message,
          errorCode,
        });
        span.recordException(error);
        span.end();
        throw e;
      }
    };
  };
}

function addTimeoutLog(message: string, timeout: number) {
  return (
    target: any,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(...params: any[]) => Promise<any>>,
  ) => {
    const func = descriptor.value;
    descriptor.value = function (...args: any[]) {
      const logger = getLogger((this as WebRtc)?.emitter?.Agent?.userInfo || null);
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          logger.debug(`${message} timeout`);
        }, timeout);
        func
          ?.apply(this, args)
          .then((e) => {
            clearTimeout(timer);
            resolve(e);
          })
          .catch((err) => {
            clearTimeout(timer);
            reject(err);
          });
      });
    };
  };
}

export const WebRtcMap: Map<string, WebRtc> = new Map([]);
type RtcType = Exclude<SessionType, 'im'>;
export class WebRtc {
  client?: Client;
  localStream?: LocalStream;
  userInfo: UserInfo;
  emitter: TcccSdk;
  localAudioLevelTimer?: ReturnType<typeof workerInterval>;
  recorder: Map<string, Recorder>;
  remoteStream: RemoteStream[];

  sdkAppId: string;
  userId: string;
  userSig: string;
  roomId: string;
  wasmWhiteList: boolean;
  type: RtcType;
  privateMapKey: string;
  sessionId: string;
  direction: Direction;
  finished?: boolean;

  aiEnabled: boolean;
  asr?: Asr;

  proxyServer?:
    | string
    | {
        websocketProxy: string;
        loggerProxy: string;
        unifiedProxy?: string;
      };

  turnServer?: TurnServerConfig | TurnServerConfig[];

  logger: ReturnType<typeof getLogger>;

  onError?: (message: string) => void;
  constructor({
    sdkAppId,
    userId,
    roomId,
    userSig,
    privateMapKey,
    sessionId,
    direction,
    type,
    emitter,
    onError,
    WASMWhiteList,
    proxyServer,
    turnServer,
  }: {
    sdkAppId: string;
    userId: string;
    roomId: string;
    userSig: string;
    privateMapKey: string;
    sessionId: string;
    direction: Direction;
    type: RtcType;
    emitter: TcccSdk;
    onError?: (message: string) => void;
    WASMWhiteList: boolean;
    proxyServer?:
      | string
      | {
          websocketProxy: string;
          loggerProxy: string;
          unifiedProxy?: string;
        };
    turnServer?: TurnServerConfig | TurnServerConfig[];
  }) {
    if (!emitter.Agent.userInfo) {
      throw new Error(i18next.t('Authorization failed'));
    }
    this.userInfo = emitter.Agent.userInfo;
    this.logger = getLogger(emitter.Agent.userInfo);
    this.recorder = new Map();
    this.sdkAppId = sdkAppId;
    this.userId = userId;
    this.roomId = roomId;
    this.userSig = userSig;
    this.privateMapKey = privateMapKey;
    this.sessionId = sessionId;
    this.direction = direction;
    this.emitter = emitter;
    this.type = type;
    this.wasmWhiteList = WASMWhiteList;
    this.aiEnabled = false;
    this.proxyServer = proxyServer;
    this.turnServer = turnServer;

    this.onError = onError;
    if (emitter && type && ['phone', 'voip'].includes(type)) {
      this.asr = new Asr({ sessionId, userId, roomId, emitter });
    }
    this.remoteStream = [];
    WebRtcMap.set(sessionId, this);
  }

  async enterRoom(span: Span, inProgress?: boolean, isStartRealtimeAsr?: boolean) {
    const trtcSpan = tracer.startSpan(
      'trtc',
      {
        attributes: pick(this.emitter.Agent.userInfo, ['sdkAppId', 'userId']),
      },
      trace.setSpan(context.active(), span),
    );
    trtcSpan.setAttributes({
      type: this.type,
      sdkAppId: this.sdkAppId,
      userId: this.userId,
      direction: this.direction,
      wasm: this.wasmWhiteList,
    });
    this.localStream = await this.createLocalStream(trtcSpan);
    if (!this.localStream) {
      const error = new Error('localStream initial failed');
      trtcSpan.recordException(error);
      trtcSpan.end();
      throw error;
    }
    if (this.wasmWhiteList) {
      try {
        const processor = await rtcAIDenoiser.createProcessor({
          sdkAppId: this.sdkAppId,
          userId: this.userId,
          userSig: this.userSig,
        });
        processor.process(this.localStream);
        await processor.enable();
        this.logger.info('create denoiser processor success');
      } catch {
        this.logger.info('create denoiser processor failed');
        // trtc 不是旗舰版 或者 未加白
        window.localStorage.setItem('wasmWhiteList', '0');
      }
    }

    this.aiEnabled = !!this.asr?.initAsrSocket();
    trtcSpan.setAttribute('asr', this.aiEnabled);
    await this.seatIn(trtcSpan, inProgress);
    this.client = await this.createClient(trtcSpan);
    await this.setProxy(trtcSpan, this.client);
    this.initClientEvent(this.client, isStartRealtimeAsr);
    await this.joinRoom(trtcSpan, this.client);
    await this.playLocalStream(trtcSpan, this.localStream);
    await this.publishLocalStream(trtcSpan, this.client, this.localStream);

    if (isStartRealtimeAsr) {
      try {
        this.asr?.startLocalAsr({ localStream: this.localStream });
      } catch (e) {
        this.logger.error('local stream start asr error', e);
      }
    }
    try {
      this.localAudioLevelTimer = workerInterval(() => {
        if (this.localStream) {
          this.localStream.getAudioLevel();
        }
      }, 1000);
    } catch (e) {}
    trtcSpan.end();
    return Promise.resolve({
      aiEnabled: this.aiEnabled,
    });
  }
  async leaveRoom(span: Span) {
    this.finished = true;
    const trtcSpan = tracer.startSpan(
      'trtc',
      {
        attributes: pick(this.emitter.Agent.userInfo, ['sdkAppId', 'userId']),
      },
      trace.setSpan(context.active(), span),
    );
    if (this.localStream) {
      this.asr?.stopLocalAsr({ localStream: this.localStream });
    }
    this.asr?.stop();
    try {
      if (this.localAudioLevelTimer) {
        trtcSpan.addEvent('clearLocalAudioLevelTimer');
        clearWorkerInterval(this.localAudioLevelTimer);
        this.localAudioLevelTimer = undefined;
      }
    } catch (e) {
      trtcSpan.recordException(e as Error);
      this.logger.warn('close audio level timer failed', e);
    }
    try {
      for (const record of this.recorder.values()) {
        record.end();
      }
    } catch (e) {
      this.logger.warn('recorder end error', e);
    }
    if (this.client) {
      this.client.off('*');
      // @ts-ignore
      this.client?.enableAudioVolumeEvaluation?.(-1);
      if (this.localStream) {
        try {
          this.localStream.stop();
          trtcSpan.addEvent('stopLocalStream');
          this.logger.info('local stream stop success');
        } catch (e) {
          trtcSpan.recordException(e as Error);
          this.logger.warn('local stream stop failed', e);
        }
        try {
          const unPublishTime = Date.now();
          await this.client.unpublish(this.localStream);
          trtcSpan.addEvent('unpublishStream');
          const duration = Date.now() - unPublishTime;
          this.logger.info('client unPublish success', `duration: ${duration}`);
        } catch (e) {
          trtcSpan.recordException(e as Error);
          this.logger.warn('client unPublish failed', e);
        }
      } else {
        trtcSpan.addEvent('emptyLocalStream');
        this.logger.warn('client unPublish failed, local stream is empty');
      }
      try {
        await this.client.leave();
        this.logger.info('client leave success');
      } catch (e) {
        this.logger.warn('client leave failed', e);
      }
      try {
        await this.client.destroy();
      } catch (e) {
        this.logger.warn('client destroy error', e);
      }
    } else {
      const log = 'client is empty';
      trtcSpan.addEvent(log);
      this.logger.warn(log);
    }
    trtcSpan.addEvent('leaveRoom success');
    trtcSpan.end();
    return Promise.resolve();
  }
  audioVolumeChanged(event: any) {
    // @ts-ignore
    const streams = event.result as { audioVolume: number; userId: string; stream: Stream }[];
    const data = streams
      .filter((stream) => stream.userId !== IVR_USER)
      .map((stream) => ({
        audioVolume: stream.audioVolume,
        sessionId: this.sessionId,
        userId: stream.userId,
      }));
    this.emitter?.emit('audioVolume', data);
  }
  unmuteVideo() {
    try {
      if (this.localStream) {
        this.localStream.unmuteVideo();
        this.logger.warn('unmuteVideo success');
      } else {
        this.logger.warn('unmuteVideo failed');
      }
    } catch (e) {
      this.logger.error('unmuteVideo error', e);
    }
  }
  muteVideo() {
    try {
      if (this.localStream) {
        this.localStream.muteVideo();
        this.logger.info('muteVideo success');
      } else {
        this.logger.warn('muteVideo failed');
      }
    } catch (e) {
      this.logger.error('muteVideo error', e);
    }
  }
  unmuteAudio() {
    try {
      if (this.localStream) {
        const isOk = this.localStream.unmuteAudio();
        this.logger.info('unmuteAudio return:', isOk);
      } else {
        this.logger.warn('unmuteAudio failed');
      }
    } catch (e) {
      this.logger.error('unmuteAudio error', e);
      throw e;
    }
  }
  muteAudio = () => {
    try {
      if (this.localStream) {
        const isOk = this.localStream.muteAudio();
        this.logger.info('muteAudio return:', isOk);
      } else {
        this.logger.warn('muteAudio failed');
      }
    } catch (e) {
      this.logger.error('muteAudio error', e);
      throw e;
    }
  };
  onStreamPlayError(error?: MediaError) {
    const message = ['stream play error', error?.message].join(' ');
    this.logger.error(message);
    this.logger.report(message);
  }

  async startRealtimeAsr() {
    const list = [];
    if (this.localStream) {
      list.push(this.asr?.startLocalAsr({ localStream: this.localStream }));
    }
    if (!!this.remoteStream.length) {
      forEach(this.remoteStream, (stream) => {
        const streamUserId = stream.getUserId();
        if (streamUserId === IVR_USER) return;
        list.push(this.asr?.startRemoteAsr({ remoteStream: stream }));
      });
    }
    await Promise.all(list);
  }

  async stopRealtimeAsr() {
    const list = [];
    if (this.localStream) {
      list.push(this.asr?.stopLocalAsr({ localStream: this.localStream }));
    }
    if (!!this.remoteStream.length) {
      forEach(this.remoteStream, (stream) => {
        const streamUserId = stream.getUserId();
        if (streamUserId === IVR_USER) return;
        list.push(this.asr?.stopRemoteAsr({ remoteStream: stream }));
      });
    }
    await Promise.all(list);
  }

  @addSpanLog('trtc/join')
  @addTimeoutLog('join 5000', 5000)
  private async joinRoom(parentSpan: Span, client: Client) {
    try {
      const joinTime = Date.now();
      await client.join({
        roomId: Number(this.roomId),
        privateMapKey: this.privateMapKey,
      });
      // joinSpan.end();
      const duration = Date.now() - joinTime;
      this.logger.info('join room success', `duration: ${duration}ms`);
      parentSpan.addEvent('joinRoomSuccess', {
        duration: `${duration}`,
      });
      if (duration > 8000) {
        this.logger.report('join room duration', `${duration}ms`);
      }
    } catch (e) {
      if (this.finished) {
        parentSpan.setAttribute('finished', true);
        parentSpan.end();
        return Promise.reject('通话已结束');
      }
      this.logger.info('join failed', e);
      parentSpan.recordException(e as Error);
      parentSpan.end();
      return Promise.reject(e);
    }
  }

  @addSpanLog('trtc/play')
  private async playLocalStream(parentSpan: Span, localStream: LocalStream) {
    try {
      if (this.type === 'video') {
        await localStream.play('mainVideo');
      } else {
        await localStream.play(this.getStreamPlayElement());
      }
      // @ts-ignore
      localStream?.audioPlayer_?.element_?.removeEventListener('error', this.onStreamPlayError);
    } catch (e) {
      this.logger.error('local stream play failed', e);
      try {
        localStream.stop();
        if (this.type === 'video') {
          await localStream.play('mainVideo');
        } else {
          await localStream.play(this.getStreamPlayElement());
        }
        parentSpan.addEvent('replayLocalStream', {
          type: this.type,
        });
        this.logger.info('local stream try to play again success');
      } catch (e) {
        parentSpan.recordException(e as Error);
        parentSpan.end();
        return Promise.reject(e);
      }
    }
  }

  @addSpanLog('trtc/publish')
  @addTimeoutLog('publish 5000', 5000)
  private async publishLocalStream(parentSpan: Span, client: Client, localStream: LocalStream) {
    if (this.type !== 'monitor') {
      try {
        await client.publish(localStream);
        parentSpan.addEvent('publishSuccess');
      } catch (e) {
        if (this.finished) {
          parentSpan.setAttribute('finished', true);
          parentSpan.end();
          return Promise.reject('通话已结束');
        }
        this.logger.info('publish failed', e);
        parentSpan.recordException(e as Error);
        parentSpan.end();
        return Promise.reject(e);
      }
    }
  }

  @addSpanLog('trtc/setProxy')
  private async setProxy(parendSpan: Span, client: Client) {
    if (this.proxyServer) {
      try {
        parendSpan.addEvent('setProxyServer', {
          proxyServer: typeof this.proxyServer === 'object' ? JSON.stringify(this.proxyServer) : this.proxyServer,
        });
        if (typeof this.proxyServer === 'string') {
          client.setProxyServer(this.proxyServer);
        } else {
          // @ts-ignore TRTC ts 类型有问题
          this.client.setProxyServer(this.proxyServer);
        }
      } catch (e) {
        this.logger.error('setProxyServer Error', e);
        parendSpan.recordException(e as Error);
        parendSpan.end();
        throw e;
      }
    }
    if (this.turnServer) {
      try {
        parendSpan.addEvent('setTurnServer', {
          turnServer: JSON.stringify(this.turnServer),
        });
        client.setTurnServer(this.turnServer);
      } catch (e) {
        this.logger.error('setTurnServer Error', e);
        parendSpan.recordException(e as Error);
        parendSpan.end();
        throw e;
      }
    }
  }

  @addSpanLog('trtc/localStream')
  private async createLocalStream(parentSpan: Span): Promise<LocalStream | undefined> {
    const video = this.type === 'video';
    const audio = true;
    try {
      const localStream = await createLocalStream({
        video,
        audio,
        userId: this.userInfo.userId,
        sdkAppId: this.userInfo.sdkAppId,
        span: parentSpan,
      });
      try {
        aegis.reportEvent({
          name: 'TRTCWhiteList',
          ext1: this.sdkAppId,
          ext2: localStorage.getItem('wasmWhiteList') || 'N/A',
          ext3: this.userId,
        });
      } catch {
        // do nothing
      }
      try {
        localStream.unmuteAudio();
      } catch (e) {
        parentSpan.recordException(e as Error);
        this.logger.error('unmuteAudio failed', e);
      }
      try {
        localStream.unmuteVideo();
      } catch (e) {
        parentSpan.recordException(e as Error);
        this.logger.error('unmuteVideo failed', e);
      }
      return localStream;
    } catch (e) {
      return undefined;
    }
  }

  @addSpanLog('trtc/createClient')
  private async createClient(parentSpan: Span): Promise<Client> {
    this.logger.info(
      'createClient',
      `sdkAppId: ${this.sdkAppId}`,
      `userId: ${this.userId}`,
      `userSig: ${this.userSig}`,
    );
    try {
      const client = TRTCJsSdk.createClient({
        sdkAppId: Number(this.sdkAppId),
        userId: this.userId,
        userSig: this.userSig,
        mode: 'rtc',
        // @ts-ignore
        bussinessInfo: JSON.stringify({
          Str_uc_params: {
            uc_biz_type: 1000,
          },
        }),
      });
      parentSpan.addEvent('createClient', {
        sdkAppId: Number(this.sdkAppId),
        userId: this.userId,
        roomId: this.roomId,
        userSig: this.userSig,
        privateMapKey: this.privateMapKey,
        mode: 'rtc',
      });

      return client;
    } catch (e) {
      parentSpan.recordException(e as Error);
      parentSpan.end();
      this.logger.error('create client failed', e);
      throw e;
    }
  }

  private async seatIn(parentSpan: Span, inProgress?: boolean) {
    if (this.direction === Direction.callIn && !inProgress) {
      const seatInSpan = tracer.startSpan(
        'trtc/seatin',
        {
          attributes: {
            type: this.type,
            wasm: this.wasmWhiteList,
            ...pick(this.emitter.Agent.userInfo, ['sdkAppId', 'userId']),
          },
        },
        trace.setSpan(context.active(), parentSpan),
      );
      try {
        parentSpan.addEvent('/ccc/pstn/seatin', {
          sessionId: this.sessionId,
        });
        await contextWithSpan(seatInSpan, () =>
          this.emitter.http.request('/ccc/pstn/seatin', {
            sessionId: this.sessionId,
          }),
        );
        seatInSpan.end();
      } catch (e) {
        const error = e as Error;
        seatInSpan
          .setAttributes({
            errorName: error.name,
            errorMessage: error.message,
            errorCode: (e as any).errorCode,
          })
          .recordException(error);
        seatInSpan.end();
        parentSpan.recordException(e as Error);
        parentSpan.end();
        throw e;
      }
    }
  }
  private initClientEvent(client: Client, isStartRealtimeAsr?: boolean) {
    client.on('error', (event) => {
      const errMsg = `trtc client errorCode: ${event.getCode()}, ${event.message}`;
      this.logger.report(errMsg);
      this.logger.error(errMsg);
      try {
        this.onError?.(errMsg);
      } catch (e) {
        this.logger.error('error callback exec failed', e);
      }
    });
    client.on('client-banned', (event) => {
      const message = ['client-banned', `reason: ${event.reason}`, `message: ${event.message}`].join(' ');
      this.logger.error(message);
      this.logger.report(message);
    });
    client.on('peer-join', () => {});
    client.on('peer-leave', () => {});
    client.on('connection-state-changed', async (event) => {
      this.logger.info('connection-state-changed', `prevState: ${event.prevState} - state: ${event.state}`);
      try {
        await this.emitter.http.request('/ccc/report/seatConnectionState', {
          roomId: +this.roomId,
          connectionState: ConnectionState[event.state] ?? ConnectionState[ConnectionState.UNKNOWN],
        });
      } catch (e) {
        this.logger.warn('report connectionState error', e);
      }
    });
    client.on('network-quality', async (event) => {
      this.emitter?.emit('networkQuality', event as any);
      try {
        await this.emitter.http.request('/ccc/report/seatNetworkQuality', {
          roomId: +this.roomId,
          networkQuality: event,
        });
      } catch (e) {
        this.logger.warn('report seatNetworkQuality error', e);
      }
    });
    client.on('audio-volume', (event) => this.audioVolumeChanged(event));
    client.on('stream-added', (event) => {
      const { stream } = event;
      const streamUserId = stream.getUserId();
      this.logger.info('stream-added', `streamId: ${stream.getId()}`, `userId: ${streamUserId}`);
      const remoteStream = event.stream;
      this.remoteStream.push(remoteStream);
      if (this.client) {
        this.client
          .subscribe(remoteStream, {
            audio: true,
            video: this.type === 'video',
          })
          .catch((e) => {
            this.logger.error('subscribe stream error', e.message);
          });
      } else {
        this.logger.error('stream-added failed', 'client is empty');
      }
    });
    client.on('stream-subscribed', async (event) => {
      const { stream } = event;
      const streamUserId = stream.getUserId();
      this.logger.info('stream-subscribed', `userId: ${stream.getId()}`, `userId: ${streamUserId}`);
      try {
        // TODO: create div
        if (streamUserId !== IVR_USER) {
          await stream.play(this.type === 'video' ? 'remoteVideo' : this.getStreamPlayElement());
        } else {
          // 视频客服隐藏IVR_USER黑屏，只放音
          await stream.play(this.getStreamPlayElement());
        }
        this.logger.info('remote stream play success', `userId: ${stream.getId()}`);
        if (this.localStream && stream) {
          // 页面刷新以及第三方通话进房时 如果已经打开实时asr则需要start
          if (stream.getUserId() !== IVR_USER && (isStartRealtimeAsr || this.asr?.checkRealtimeAsrHasBeenOpened())) {
            try {
              this.asr?.startRemoteAsr({ remoteStream: stream });
            } catch (e) {
              this.logger.error(
                'remote stream start asr error',
                `sessionId: ${this.sessionId}`,
                `userId: ${stream.getId()}`,
                e,
              );
            }
          }
          const recorder = new Recorder(
            stream,
            this.localStream,
            {
              remoteId: stream.getId(),
              seatUserId: this.userId,
              sessionId: this.sessionId,
              roomId: this.roomId,
              sdkAppId: this.sdkAppId,
            },
            this.emitter,
          );
          recorder.start();
          this.recorder.set(stream.getId(), recorder);
          // @ts-ignore
          stream?.audioPlayer_?.element_?.addEventListener('error', this.onStreamPlayError);
        }
      } catch (e) {
        this.logger.error('remote stream play failed', e);
      }
    });
    client.on('stream-removed', (event) => {
      const { stream } = event;
      const streamUserId = stream.getUserId();
      stream.stop();
      if (streamUserId !== IVR_USER) {
        this.recorder.get(stream.getId())?.end();
        this.asr?.stopRemoteAsr({ remoteStream: stream });
      }
    });
    client.on('stream-updated', (event) => {
      const { stream } = event;
      this.logger.info('stream-updated', `userId: ${stream.getId()}`);
    });
    client.enableAudioVolumeEvaluation(500);
  }

  private getStreamPlayElement() {
    const id = `${this.sdkAppId + this.userId}123123`;
    const elementId = `${id}-rtc-session-audio`;
    let element = document.getElementById(elementId) as HTMLDivElement;

    this.logger.debug('get audio container', element);
    if (!element) {
      this.logger.debug('create audio container');
      element = document.createElement('div');
      element.setAttribute('id', elementId);
      element.setAttribute('style', 'width:0;height:0;overflow:hidden');
      document.body.appendChild(element);
      this.logger.debug('create audio container id:%o', elementId);
    }

    return element;
  }
}
